import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import type {
  BackgroundColorProps,
  BorderColorProps,
  BoxShadowProps,
  WidthProps,
} from 'styled-system';
import { background, borderColor, boxShadow, color, space, themeGet, width } from 'styled-system';

import { ButtonLink } from 'common/components/Button';
import { Flex } from 'common/components/Flex';
import RichContent from 'common/components/RichText/RichContent';
import type { Color } from 'common/theme/colors/types';
import type { ColorProps, SpaceProps } from 'common/theme/system-types';
import { PictureTextKind } from '../enums';
import { dispatchPictureTextClickEvent } from '../tracking';
import { getTitleFromDescription } from '../utils';

export interface TextBlockProps {
  kind: PictureTextKind;
  revert?: boolean;
  description?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  ctaTarget?: string;
  ctaLabelColor?: string;
  ctaBackgroundColor?: string;
}

const UnstyledDescription = styled(Flex)``;

const Description = (props: ComponentProps<typeof UnstyledDescription>) => (
  <UnstyledDescription flexDirection="column" alignItems="flex-start" flexGrow={1} {...props} />
);

const UnstyledRichText = styled(RichContent)<WidthProps & SpaceProps>`
  ${width};
  ${space};
`;

const RichText = (props: ComponentProps<typeof UnstyledRichText>) => (
  <UnstyledRichText width={1} py="sp_8" {...props} />
);

const UnstyledCtaLink = styled(ButtonLink)<
  SpaceProps & ColorProps & BackgroundColorProps & BoxShadowProps & BorderColorProps
>`
  ${space};
  ${color};
  ${background};
  ${boxShadow};
  ${borderColor};

  &:active,
  &:hover,
  &:visited {
    ${borderColor};
    color: ${({ color: c }) => c || themeGet('colors.sys.neutral.background.default')};
    background: ${({ background: bg }) => bg || themeGet('colors.sys.primary.background.default')};
  }
`;

const CtaLink = (props: ComponentProps<typeof UnstyledCtaLink>) => (
  <UnstyledCtaLink boxShadow="none" px="sp_16" {...props} />
);

const getContainerWidth = (kind: PictureTextKind) => {
  switch (kind) {
    case PictureTextKind.DoublePictureText:
    case PictureTextKind.FullWidthPicture:
      return 1;
    case PictureTextKind.HalfPictureText:
      return [1, 1 / 2];
    default:
      return [1, 1 / 3];
  }
};

const TextBlock = ({
  kind,
  revert,
  description,
  ctaLabel,
  ctaUrl,
  ctaLabelColor,
  ctaBackgroundColor,
  ctaTarget,
}: TextBlockProps) => {
  const hasSpacing = [PictureTextKind.DoublePictureText, PictureTextKind.FullWidthPicture].includes(
    kind
  );
  const descriptionSpace = {
    [revert ? 'pr' : 'pl']: [null, 'sp_24', null, 'sp_32'],
  };

  const onClick = useCallback(() => {
    const title = getTitleFromDescription(description);

    dispatchPictureTextClickEvent(kind, ctaLabel, title || '');
  }, [description, kind, ctaLabel]);

  return (
    <Description
      width={getContainerWidth(kind)}
      justifyContent={hasSpacing ? 'flex-start' : 'center'}
      {...(!hasSpacing ? descriptionSpace : {})}
    >
      <RichText content={description} />
      {ctaUrl && ctaLabel && (
        <CtaLink
          href={ctaUrl}
          color={(ctaLabelColor ?? 'sys.neutral.background.default') as Color}
          background={ctaBackgroundColor ?? 'sys.primary.background.default'}
          borderColor={ctaBackgroundColor ?? 'sys.primary.background.default'}
          {...(ctaTarget && { target: ctaTarget })}
          onClick={onClick}
        >
          {ctaLabel}
        </CtaLink>
      )}
    </Description>
  );
};

export default TextBlock;
